import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Trash2, Flag, ExternalLink, Edit2, Mail, CheckCircle2, FileText,
} from "lucide-react";
import { InspectionEmailDialog } from "@/components/workOrderTab/InspectionEmailDialog";
import { InspectionFallbackDialog } from "@/components/workOrderTab/InspectionFallbackDialog";
import { btn } from "@/components/common/btn";

// Inspections block on the WO's Field tab (UI sweep batch 4: no collapse —
// the tab is the section). Heading + green "+ Inspection" + count, then
// the linked inspections with conduct/view/form/email/edit/delete per row.
//
// Update / reopen uses a dynamic import to avoid a circular dep on
// `@/lib/api` from this leaf component.
export function WOInspectionsList({
  editingWO,
  woInspections,
  setAddInspForm, setShowAddInspDialog,
  inspStats,
  handleDeleteInspection,
  loadWoInspections,
}) {
  const [emailFor, setEmailFor] = useState(null);
  const [fallbackFor, setFallbackFor] = useState(null);
  if (!editingWO) return null;
  return (
    <div className="space-y-2" data-testid="wo-inspections-section">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="font-semibold text-sm">Inspections</h4>
        <button
          type="button"
          className={btn("write")}
          onClick={() => {
            setAddInspForm({ template_id: "", inspector_name: "" });
            setShowAddInspDialog(true);
          }}
          data-testid="btn-add-wo-inspection"
        >
          <Plus className="w-3 h-3" /> Inspection
        </button>
        <span className="ml-auto text-xs text-slate-500 num">{woInspections.length} inspection{woInspections.length !== 1 ? "s" : ""}</span>
      </div>
      {woInspections.length === 0 ? (
        <div className="text-center py-4 text-slate-400 text-xs border rounded-lg border-dashed">
          No inspections linked to this work order
        </div>
      ) : (
        <div className="space-y-2">
          {woInspections.map((insp) => {
            const st = inspStats(insp);
            return (
              <div
                key={insp.id}
                className="border rounded-lg p-3 bg-white hover:bg-slate-50/50 transition-colors"
                data-testid={`wo-inspection-${insp.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-slate-900 truncate">
                        {insp.template_name}
                      </span>
                      <Badge
                        className={`border-0 text-[10px] ${
                          insp.status === "complete"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {insp.status === "complete" ? "Complete" : "In progress"}
                      </Badge>
                      {insp.needs_review && (
                        <Badge
                          className="border-0 text-[10px] bg-red-100 text-red-700"
                          title={insp.conflict_flag
                            ? "This copy arrived from a technician's device after the job was already completed here — compare both versions."
                            : "A second version of this inspection arrived from the field — compare both versions."}
                          data-testid={`inspection-needs-review-${insp.id}`}
                        >
                          <Flag className="w-3 h-3 mr-1" />Needs review
                        </Badge>
                      )}
                      {insp.source === "offline" && (
                        <Badge className="border-0 text-[10px] bg-slate-100 text-slate-600" data-testid={`inspection-offline-${insp.id}`}>
                          Synced from field
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-400">
                      <span>{insp.inspector_name}</span>
                      <span>{insp.conducted_date}</span>
                      {(insp.locations || []).length > 0 && (
                        <span className="text-slate-500">
                          {(insp.locations || [])
                            .map((l) => l.identifier ? `${l.name} (${l.identifier})` : l.name)
                            .join(", ")}
                        </span>
                      )}
                      {st.flagged > 0 && (
                        <span className="text-red-500 flex items-center gap-0.5">
                          <Flag className="w-3 h-3" />{st.flagged}
                        </span>
                      )}
                      {st.total > 0 && <span>{st.passRate}% pass</span>}
                      {insp.emailed_at && (
                        <span className="text-emerald-600 flex items-center gap-0.5" data-testid={`inspection-sent-${insp.id}`}>
                          <CheckCircle2 className="w-3 h-3" />
                          Sent to {insp.emailed_to} on {new Date(insp.emailed_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      className={btn("nav")}
                      onClick={() => window.open(`/inspections/${insp.id}`, "_blank")}
                      data-testid={`open-inspection-${insp.id}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      {insp.status === "complete" ? "View" : "Conduct"}
                    </button>
                    <button
                      type="button"
                      className={btn("nav")}
                      onClick={() => setFallbackFor(insp)}
                      title="Paper fallback — export a fillable PDF for a tech who can't use the app, and import it back"
                      data-testid={`fallback-inspection-${insp.id}`}
                    >
                      <FileText className="w-3 h-3" />Form
                    </button>
                    {insp.status === "complete" && (
                      <button
                        type="button"
                        className={btn("data")}
                        onClick={() => setEmailFor(insp)}
                        title={insp.emailed_at ? `Sent to ${insp.emailed_to} on ${new Date(insp.emailed_at).toLocaleDateString()}` : "Email the report to the customer"}
                        data-testid={`email-inspection-${insp.id}`}
                      >
                        <Mail className="w-3 h-3" />{insp.emailed_at ? "Resend" : "Email"}
                      </button>
                    )}
                    {insp.status === "complete" && (
                      <button
                        type="button"
                        className={btn("mode")}
                        onClick={async () => {
                          try {
                            await import("@/lib/api").then((m) =>
                              m.updateInspection(insp.id, { status: "in_progress" }),
                            );
                            toast.success("Inspection reopened for editing");
                            loadWoInspections(editingWO.id);
                            window.open(`/inspections/${insp.id}`, "_blank");
                          } catch {
                            toast.error("Failed to reopen");
                          }
                        }}
                        data-testid={`edit-inspection-${insp.id}`}
                      >
                        <Edit2 className="w-3 h-3" />Edit
                      </button>
                    )}
                    <button
                      type="button"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteInspection(insp.id)}
                      title="Delete inspection"
                      data-testid={`delete-wo-inspection-${insp.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {emailFor && (
        <InspectionEmailDialog
          inspection={emailFor}
          wo={editingWO}
          onSent={() => loadWoInspections(editingWO.id)}
          onClose={() => setEmailFor(null)}
        />
      )}
      {fallbackFor && (
        <InspectionFallbackDialog
          inspection={fallbackFor}
          onImported={() => loadWoInspections(editingWO.id)}
          onClose={() => setFallbackFor(null)}
        />
      )}
    </div>
  );
}
